import Vue from "vue";
import VueApollo from "vue-apollo";
import { createApolloClient, restartWebsockets } from "vue-cli-plugin-apollo/graphql-client";
import { loadHandler } from "./global/loading";
import UserQuery from "@/graphql/queries/User";
import UserPermissionsQuery from "@/graphql/queries/UserPermissions";
import CompanySettingsQuery from "@/graphql/queries/CompanySettings";
import { uniq } from "lodash";
import { InMemoryCache } from "apollo-cache-inmemory";

Vue.use(VueApollo);

const AUTH_TOKEN = "w-4u+h";

const defaultOptions = {
	// You can use `wss` for secure connection (recommended in production)
	// Use `null` to disable subscriptions
	wsEndpoint: null,
	tokenName: AUTH_TOKEN,
	// Enable Automatic Query persisting with Apollo Engine
	persisting: false,
	// Use websockets for everything (no HTTP)
	// You need to pass a `wsEndpoint` for this to work
	websocketsOnly: false,
	// Is being rendered on the server?
	ssr: false,

	// Override default apollo link
	// note: don't override httpLink here, specify httpLink options in the
	// httpLinkOptions property of defaultOptions.
	// link: myLink

	// Override default cache
	cache: new InMemoryCache()

	// Override the way the Authorization header is set
	// getAuth: (tokenName) => ...

	// Additional ApolloClient options
	// apollo: { ... }

	// Client local data (see apollo-link-state)
	// clientState: { resolvers: { ... }, defaults: { ... } }
};

// Call this in the Vue app file
export function createProvider(options = {}) {
	const { apolloClient, wsClient } = createApolloClient({
		...defaultOptions,
		...options,
		httpEndpoint: process.env.VUE_APP_GRAPHQL_HTTP || "/graphql",
		tokenName: AUTH_TOKEN
	});

	const { apolloClient: apolloClientPublic, wsClient: wsClientPublic } = createApolloClient({
		...defaultOptions,
		...options,
		httpEndpoint: process.env.VUE_APP_GRAPHQL_HTTP_PUBLIC || "/public-graphql"
	});

	apolloClient.wsClient = wsClient;
	apolloClientPublic.wsClient = wsClientPublic;

	window.apolloClient = apolloClient;
	window.apolloClientPublic = apolloClientPublic;

	// Create vue apollo provider
	const apolloProvider = new VueApollo({
		defaultClient: apolloClient,
		clients: {
			private: apolloClient,
			public: apolloClientPublic
		},
		defaultOptions: {
			$query: {
				// fetchPolicy: 'cache-and-network',
			}
		},
		watchLoading(isLoading, countModifier) {
			loadHandler(isLoading, countModifier);
		},
		errorHandler(error) {
			if (error.message.includes("Permission Error:")) {
				this.$router.push({ name: "auth.unauthorized" });
				this.$message.error("You are not authorised to perform this action");
				if (typeof localStorage !== "undefined") localStorage.clear();
			}
			if (error.message.includes("Rate Error:")) {
				this.$message.error("This action has exceeded the rate limit");
			}
			// eslint-disable-next-line no-console
			console.log("%cError", "background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;", error.message);
		}
	});

	return apolloProvider;
}

// Manually call this when user log in
export async function onLogin(token) {
	if (typeof localStorage !== "undefined" && token) {
		localStorage.clear();
		localStorage.setItem(AUTH_TOKEN, token);

		let [user, permissionsResponse, companySettingsResponse] = await Promise.all([
			window.apolloClient.query({
				query: UserQuery
			}),
			window.apolloClient.query({
				query: UserPermissionsQuery
			}),
			window.apolloClient.query({
				query: CompanySettingsQuery
			})
		]);

		user = user.data.User;

		window.Intercom("boot", {
			app_id: "salq7gk9",
			email: user.email,
			name: `${user.first_name} ${user.last_name}`,
			user_id: user.id
		});

		let userPermissions = uniq(permissionsResponse.data.UserPermissions.map(p => p.name));
		let companySettings = companySettingsResponse.data.CompanySettings;

		if (companySettings.can_run_reports == 0) {
			userPermissions = userPermissions.filter(permissions => permissions !== "run-reports")
		}

		localStorage.setItem("user-permissions", JSON.stringify(userPermissions));
		localStorage.setItem("company-settings", JSON.stringify(companySettings));
	}

	try {
		await window.apolloClient.resetStore();
		await window.apolloClientPublic.resetStore();
		if (window.apolloClient.wsClient) restartWebsockets(window.apolloClient.wsClient);
		if (window.apolloClientPublic.wsClient) restartWebsockets(window.apolloClientPublic.wsClient);
	} catch (e) {
		// eslint-disable-next-line no-console
		console.log("%cError on cache reset (login)", "color: orange;", e.message);
	}
}

// Manually call this when user log out
export async function onLogout() {
	try {
		window.Intercom("shutdown");
		await window.apolloClient.resetStore();
		await window.apolloClientPublic.resetStore();
		if (window.apolloClient.wsClient) restartWebsockets(window.apolloClient.wsClient);
		if (window.apolloClientPublic.wsClient) restartWebsockets(window.apolloClientPublic.wsClient);
	} catch (e) {
		// eslint-disable-next-line no-console
		console.log("%cError on cache reset (logout)", "color: orange;", e.message);		
	}
	if (typeof localStorage !== "undefined") await localStorage.clear();
}

export const authKey = AUTH_TOKEN;
